import React, {useEffect} from 'react';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {usePrevious} from '../../../../../srcCommon/helpers/hookHelpers';
import {FilterResourceResults, FilterRow} from '../common/types';
import {
  MultiSelectFilterDropdown,
  MultiSelectFilterDropdownProps,
} from '../multiSelectFilter/multiSelectFilterDropdown';
import {FilterDropdownResults, FilterDropdownSource, FilterRowOf} from './filterDropdownSource';
import {useFilterDropdownSource} from './useFilterDropdownSource';

/*
 * Props.
 */

export interface FilterSourceDropdownProps<T>
  extends Omit<MultiSelectFilterDropdownProps, 'rows' | 'results'> {
  // Data source used to load the rows and results for the filter
  source: FilterDropdownSource<T>;

  // Function for generating filter resource rows from fetcher results.
  filterResourceOf: FilterRowOf<T>;

  // Optional empty rendering function, called with the current query.
  renderEmpty?: RendererOf<string>;
}

/*
 * Component.
 */

export const FilterSourceDropdown = <T,>(props: FilterSourceDropdownProps<T>) => {
  const {searchProps, source, renderEmpty, clearButtonContent} = props;
  const {asyncStatus, performSearch, loadMore, results} = useFilterDropdownSource(
    source,
    searchProps?.searchQuery,
  );
  const prevSearchProps = usePrevious(props.searchProps);

  useInitialEffect(() => {
    performSearch();
  });

  // Update the results if the searchQuery changes
  useEffect(() => {
    const previousQuery = (prevSearchProps?.searchQuery ?? '').trim();
    const searchQuery = (props.searchProps?.searchQuery ?? '').trim();
    if (previousQuery !== searchQuery) {
      performSearch();
    }
  }, [performSearch, prevSearchProps?.searchQuery, props.searchProps?.searchQuery]);

  const rows: ReadonlyArray<FilterRow> = results ? results.items.map(props.filterResourceOf) : [];
  const dropdownResults = computeFilterResourceResults(
    asyncStatus,
    performSearch,
    loadMore,
    renderEmpty,
    results,
  );

  const {title, isMultiSelect, showClearButton, onClear, footerHandlers, ...popoverProps} = props;

  return (
    <MultiSelectFilterDropdown
      {...popoverProps}
      rows={rows}
      results={dropdownResults}
      title={title}
      searchProps={searchProps}
      isMultiSelect={isMultiSelect}
      showClearButton={showClearButton}
      onClear={onClear}
      clearButtonContent={clearButtonContent}
      footerHandlers={footerHandlers}
    />
  );
};

/*
 * Helpers.
 */

function computeFilterResourceResults<T>(
  asyncStatus: AsyncStatusesEnum,
  performSearch: Handler,
  loadMore: Handler,
  renderEmpty?: RendererOf<string>,
  results?: FilterDropdownResults<T>,
): FilterResourceResults {
  const baseKey = 'filter-source-dropdown-results';

  // If intial load hasn't completed (or completed and failed),
  // just return async status and retry.
  if (!results) {
    return {key: baseKey, asyncStatus, onLoadingRetryClicked: performSearch};
  }

  return {
    key: `${baseKey}-${results.query}`,
    asyncStatus,
    onLoadingRetryClicked: performSearch,
    loadMore,
    canLoadMore: results.canLoadMore,
    renderEmpty: renderEmpty && renderEmpty(results.query),
  };
}
